import useAuth from '@shared/utils/hooks/useAuth';

import { client, service } from '../utils/trpc';

const VIEWED_GUIDES_KEY = 'viewedFactiiiGuides';
const BASIC_GUIDE_COMPLETED_KEY = 'basicGuideCompleted';

const useGuideStorage = () => {
  const { currentUser } = useAuth({ client, service });

  const getViewedGuides = (): number[] => {
    try {
      // Only access localStorage on the client side
      if (typeof window === 'undefined') {
        return [];
      }

      const stored = localStorage.getItem(VIEWED_GUIDES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading viewed guides from localStorage:', error);
      return [];
    }
  };

  const addViewedGuide = async (factiiiId: number) => {
    try {
      if (currentUser.isAuthenticated) {
        // For logged-in users, send to server as well
        await client.guides.recordGuideView.mutate({
          factiiiId
        });
      }

      // Only access localStorage on the client side
      if (typeof window === 'undefined') {
        return;
      }

      const viewedGuides = getViewedGuides();
      if (!viewedGuides.includes(factiiiId)) {
        viewedGuides.push(factiiiId);
        localStorage.setItem(VIEWED_GUIDES_KEY, JSON.stringify(viewedGuides));
      }
    } catch (error) {
      console.error('Error saving viewed guide to localStorage:', error);
    }
  };

  const hasViewedGuide = (factiiiId: number): boolean => {
    const viewedGuides = getViewedGuides();
    return viewedGuides.includes(factiiiId);
  };

  const getBasicGuideCompleted = (): boolean => {
    try {
      // Only access localStorage on the client side
      if (typeof window === 'undefined') {
        return false;
      }

      //get from localStorage first
      const stored = localStorage.getItem(BASIC_GUIDE_COMPLETED_KEY);
      if (currentUser.isAuthenticated) {
        //if authenticated also get from database
        const completed = Boolean(currentUser.onboardingPath);
        //if database one differs from the local one, update the local one
        if (completed !== (stored === 'true'))
          localStorage.setItem(BASIC_GUIDE_COMPLETED_KEY, completed.toString());
        return completed;
      }
      // if not authenticated just return the local one
      return stored === 'true';
    } catch (error) {
      console.error(
        'Error reading basic guide completion from localStorage:',
        error
      );
      return false;
    }
  };

  const syncViewedGuidesToServer = async (): Promise<boolean> => {
    try {
      const viewedGuides = getViewedGuides();
      const basicGuideCompleted = getBasicGuideCompleted();

      // If no data to sync, return true
      if (viewedGuides.length === 0 && !basicGuideCompleted) {
        return true;
      }

      const [error, result] = await client.guides.syncLocalStorageViews.mutate({
        viewedFactiiiIds: viewedGuides,
        basicGuideCompleted
      });

      if (error) {
        return false;
      }

      if (result?.success) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error syncing viewed guides to server:', error);
      return false;
    }
  };

  return {
    getViewedGuides,
    addViewedGuide,
    hasViewedGuide,
    getBasicGuideCompleted,
    syncViewedGuidesToServer
  };
};

export default useGuideStorage;
